import React, { useState } from 'react';
import { TextField } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { createPortal } from 'react-dom';
import CommonPopUp from '../../../components/popup/common-popup';
import { AnalyticsService } from '../../../core/services/analytics.service';
import { LocalService } from '../../../core/services';
import * as gtUtil from '../../../core/utility';

const analyticsService = new AnalyticsService();
const localService = new LocalService();

interface Props {
    openModal: boolean;
    onClose: () => void;
    /** sheet_brand_handles_data.ref_id of the Brand Pulse brand being viewed. */
    refId: any;
    /** Display name, used for the default campaign title. */
    brandName: string;
    /** Logged-in user — the created campaign is owned by their brand account. */
    UserReducer: any;
    /** Fired after a successful create so the caller can refresh its campaign list. */
    onCreated: (campaign_id: number) => void;
}

/**
 * Brand Pulse :: create the private tracking campaign for a brand.
 *
 * Hits POST /brand-pulse/create-campaign-id, which writes:
 *   brd_campaigns (campaign_type 'Brand Pulse', visibility Private, status Live;
 *     until Oct 2026 brand_plans + legacy brand_campaigns 'brand_pulse')
 *   -> MYSQL_DB_NAME_2.lnk_campaignid_refid (campaign_id, ref_id)
 *
 * The link row is what makes the Campaign Tracking tab light up for this ref_id — until one
 * exists the tab stays disabled, which is the intended "no campaign yet" signal.
 */
const CreateBrandPulseCampaignPopup: React.FC<Props> = ({
    openModal,
    onClose,
    refId,
    brandName,
    UserReducer,
    onCreated,
}) => {
    const [campaignTitle, setCampaignTitle] = useState(
        brandName ? `${brandName} Brand Pulse Campaign` : ''
    );
    const [creating, setCreating] = useState(false);
    const [error, setError] = useState('');

    const handleCreate = async (event: any) => {
        event.preventDefault();
        setError('');

        if (!campaignTitle.trim()) {
            setError('Campaign name is required.');
            return;
        }
        if (!refId) {
            setError('Brand details are still loading, please try again.');
            return;
        }
        if (!UserReducer?.brand_id || !UserReducer?.user_id) {
            setError('Could not identify your brand account. Please refresh and try again.');
            return;
        }

        try {
            // `creating` also guards against a double click — each call inserts a new campaign
            setCreating(true);
            const res = await analyticsService.createBrandPulseCampaign({
                ref_id: refId,
                brand_name: brandName,
                brand_id: UserReducer.brand_id,
                user_id: UserReducer.user_id,
                campaign_title: campaignTitle.trim(),
            });

            if (res?.status === gtUtil.Constants.SUCCESS && res?.data?.campaign_id) {
                localService.toastify('Campaign created', 'success', 2000);
                onCreated(res.data.campaign_id);
                onClose();
            } else {
                setError(res?.info || 'Could not create the campaign, please try again.');
            }
        } catch (err) {
            setError('Could not create the campaign, please try again.');
        } finally {
            setCreating(false);
        }
    };

    return createPortal(
        <CommonPopUp
            className='modal-medium create-brand-pulse-campaign'
            openModal={openModal}
            modalTitle='Create Brand Pulse Campaign'
            onClose={onClose}
        >
            <form onSubmit={handleCreate}>
                <TextField
                    className='w-100 input-common mb-3'
                    size='small'
                    label='Campaign Name'
                    variant='outlined'
                    value={campaignTitle}
                    inputProps={{ maxLength: 255 }}
                    onChange={(e) => {
                        setCampaignTitle(e.target.value);
                        if (error) setError('');
                    }}
                />

                <div className='d-flex align-items-center gap-2'>
                    <LoadingButton
                        className='btn btn-primary btn-mui'
                        variant='contained'
                        type='submit'
                        loading={creating}
                    >
                        Create Campaign
                    </LoadingButton>
                    <button
                        type='button'
                        className='btn btn-outline-primary btn-mui'
                        onClick={onClose}
                    >
                        Cancel
                    </button>
                </div>

                {error && <span className='fs-14 d-block mt-2 error'>{error}</span>}

                <span className='fs-14 d-block mt-2' style={{ color: '#787878' }}>
                    This campaign is private to Brand Pulse — it is not shown on the influencer
                    side. Once created, it appears under Campaign Tracking where you can add posts
                    to it.
                </span>
            </form>
        </CommonPopUp>,
        document.body
    );
};

export default CreateBrandPulseCampaignPopup;
